import { useCallback, useEffect, useState } from 'react';
import {
  claimMediaFocus,
  onMediaFocusClaim,
  releaseMediaFocus,
} from './mediaFocus';

const MUTE_STORAGE_KEY = 'synth-web-muted';
const AUDIO_EVENT = 'synth-audio-state';

type AudioEventDetail =
  | { kind: 'playing'; playing: boolean }
  | { kind: 'muted'; muted: boolean };

interface AudioBus {
  el: HTMLAudioElement | null;
  playing: boolean;
  muted: boolean;
}

const bus: AudioBus = {
  el: null,
  playing: false,
  muted: readInitialMute(),
};

function readInitialMute(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function dispatch(detail: AudioEventDetail): void {
  window.dispatchEvent(
    new CustomEvent<AudioEventDetail>(AUDIO_EVENT, { detail }),
  );
}

function ensureEl(): HTMLAudioElement {
  if (bus.el) return bus.el;
  const el = new Audio();
  el.preload = 'auto';
  // Stage 43 fix: must set BEFORE the first src/play and BEFORE any
  // createMediaElementSource() call. Without it, an analyser tap on
  // cross-origin R2 audio reads only zeros — speech plays but the DOT
  // visualizer sees a flat spectrum and never reacts. /api/resource/*
  // already returns Access-Control-Allow-Origin via withCors().
  el.crossOrigin = 'anonymous';
  el.muted = bus.muted;
  el.addEventListener('playing', () => {
    bus.playing = true;
    // Stage 12 (A4): actual playback claims media focus.
    claimMediaFocus('tts');
    dispatch({ kind: 'playing', playing: true });
  });
  el.addEventListener('pause', () => {
    bus.playing = false;
    releaseMediaFocus('tts');
    dispatch({ kind: 'playing', playing: false });
  });
  el.addEventListener('ended', () => {
    bus.playing = false;
    releaseMediaFocus('tts');
    dispatch({ kind: 'playing', playing: false });
  });
  el.addEventListener('error', () => {
    bus.playing = false;
    releaseMediaFocus('tts');
    dispatch({ kind: 'playing', playing: false });
  });
  // Stage 12 (A4): pause our element whenever another surface claims focus.
  onMediaFocusClaim(({ owner }) => {
    if (owner !== 'tts' && bus.el && !bus.el.paused) {
      try {
        bus.el.pause();
      } catch {
        /* ignore */
      }
    }
  });
  bus.el = el;
  return el;
}

export async function playAudio(url: string): Promise<void> {
  const el = ensureEl();
  el.muted = bus.muted;
  el.src = url;
  try {
    await el.play();
  } catch {
    // Autoplay rejection — surface via state only, caller may retry.
    bus.playing = false;
    dispatch({ kind: 'playing', playing: false });
  }
}

export function stopAudio(): void {
  const el = bus.el;
  if (!el) return;
  try {
    el.pause();
    el.currentTime = 0;
  } catch {
    /* ignore */
  }
}

export function setMuted(next: boolean): void {
  bus.muted = next;
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* ignore */
  }
  if (bus.el) bus.el.muted = next;
  if (next) stopAudio();
  dispatch({ kind: 'muted', muted: next });
}

export function isMuted(): boolean {
  return bus.muted;
}

export function isPlaying(): boolean {
  return bus.playing;
}

// Stage 43: expose the singleton element + a shared AudioContext +
// shared "speech bus" to consumers. The speech bus is the single tap
// point any speech/music source should connect into so the DOT
// visualizer sees them all mixed.
//
// Topology:
//   bus.el (HTMLAudioElement) ─┐
//                              ├→ speechBus (GainNode) → analyser → ctx.destination
//   ZeroTTSPlayer atoms ───────┘
//
// All accessors are idempotent. `getOrCreateSpeechBus()` is the
// canonical entry point; the older `getAudioElement` /
// `getOrCreateAudioContext` are kept for callers that don't need the
// graph nodes.

let sharedCtx: AudioContext | null = null;
let sharedSpeechBus: GainNode | null = null;
let sharedAnalyser: AnalyserNode | null = null;
let busElSourceAttached = false;

export function getAudioElement(): HTMLAudioElement {
  return ensureEl();
}

export function getOrCreateAudioContext(): AudioContext {
  if (sharedCtx) return sharedCtx;
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  sharedCtx = new Ctx();
  return sharedCtx;
}

export interface SpeechBus {
  ctx: AudioContext;
  speechBus: GainNode;
  analyser: AnalyserNode;
}

/**
 * Returns the shared speech-visualization bus. Idempotent.
 *
 * Routes the singleton `<audio>` element through `speechBus` on first
 * call (createMediaElementSource can only be invoked once per element,
 * so this is the sole owner). External sources (e.g. ZeroTTSPlayer
 * BufferSources) should connect to `speechBus` rather than directly to
 * `ctx.destination` so they show up in the DOT analyser.
 */
export function getOrCreateSpeechBus(): SpeechBus {
  const ctx = getOrCreateAudioContext();
  if (!sharedSpeechBus) sharedSpeechBus = ctx.createGain();
  if (!sharedAnalyser) {
    sharedAnalyser = ctx.createAnalyser();
    sharedAnalyser.fftSize = 2048;
    sharedAnalyser.smoothingTimeConstant = 0.8;
    sharedAnalyser.minDecibels = -85;
    sharedAnalyser.maxDecibels = -20;
    sharedSpeechBus.connect(sharedAnalyser);
    sharedAnalyser.connect(ctx.destination);
  }
  if (!busElSourceAttached) {
    const el = ensureEl();
    try {
      const src = ctx.createMediaElementSource(el);
      src.connect(sharedSpeechBus);
      busElSourceAttached = true;
    } catch (err) {
      // createMediaElementSource throws if the element is already
      // attached — log once and continue. The analyser will still see
      // ZeroTTS atoms even if bus.el somehow ended up routed elsewhere.
      console.warn('[speech-bus] bus.el attach skipped:', err);
      busElSourceAttached = true;
    }
  }
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {
      /* will retry on next user gesture */
    });
  }
  return { ctx, speechBus: sharedSpeechBus, analyser: sharedAnalyser };
}

export interface UseAudioPlayerResult {
  play: (url: string) => Promise<void>;
  stop: () => void;
  muted: boolean;
  setMuted: (next: boolean) => void;
  playing: boolean;
}

export function useAudioPlayer(): UseAudioPlayerResult {
  const [muted, setMutedState] = useState<boolean>(bus.muted);
  const [playing, setPlayingState] = useState<boolean>(bus.playing);

  useEffect(() => {
    const onEvt = (ev: Event): void => {
      const detail = (ev as CustomEvent<AudioEventDetail>).detail;
      if (!detail) return;
      if (detail.kind === 'playing') setPlayingState(detail.playing);
      else if (detail.kind === 'muted') setMutedState(detail.muted);
    };
    window.addEventListener(AUDIO_EVENT, onEvt);
    return (): void => window.removeEventListener(AUDIO_EVENT, onEvt);
  }, []);

  const play = useCallback((url: string) => playAudio(url), []);
  const stop = useCallback(() => stopAudio(), []);
  const setMutedCb = useCallback((next: boolean) => setMuted(next), []);

  return { play, stop, muted, setMuted: setMutedCb, playing };
}
